/**
 * SequenceTracker Component
 * 
 * Tracks progress through a sequence of chess moves, handling timing and success/failure.
 * Now includes undo functionality for enhanced user experience.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { ChessMove } from '../../../types/chess';
import styles from './SequenceTracker.module.css';

// Define the SequenceStatus type to be added to chess.ts later
export type SequenceStatus = 'in-progress' | 'completed' | 'failed';

/**
 * Props for the SequenceTracker component
 */
export interface SequenceTrackerProps {
  /** Array of moves in algebraic notation */
  moveSequence: string[];
  /** Current position in the sequence (0-based) */
  currentMoveIndex: number;
  /** Optional time constraint in seconds */
  timeLimit?: number;
  /** Callback for sequence completion */
  onComplete: (success: boolean) => void;
  /** Starting position in FEN notation (optional) */
  startingPosition?: string;
  /** Whether to show a timer (defaults to true if timeLimit is provided) */
  showTimer?: boolean;
  /** Optional CSS class name */
  className?: string;
  /** Whether undo functionality is enabled (default: true) */
  enableUndo?: boolean;
  /** Callback for undo action - returns true if undo was successful */
  onUndo?: () => boolean;
  /** Whether undo is currently available */
  canUndo?: boolean;
  /** Maximum number of undos allowed (default: unlimited) */
  maxUndos?: number;
}

/**
 * Memoized SequenceTracker - Tracks progress through a sequence of chess moves
 * 
 * This component handles the tracking of move sequences, including timing
 * and success/failure states.
 */
const SequenceTrackerInternal: React.FC<SequenceTrackerProps> = ({
  moveSequence,
  currentMoveIndex,
  timeLimit,
  onComplete,
  startingPosition,
  showTimer = !!timeLimit,
  className,
  enableUndo = true,
  onUndo,
  canUndo,
  maxUndos,
}) => {
  // Track the completed moves
  const [completedMoves, setCompletedMoves] = useState<string[]>([]);
  
  // Track the remaining time if a timeLimit is provided
  const [timeRemaining, setTimeRemaining] = useState<number>(timeLimit || 0);
  
  // Track the status of the sequence
  const [sequenceStatus, setSequenceStatus] = useState<SequenceStatus>('in-progress');

  // Track undo usage
  const [undosUsed, setUndosUsed] = useState<number>(0);

  // Effect to update completedMoves when currentMoveIndex changes
  useEffect(() => {
    if (currentMoveIndex > 0) {
      setCompletedMoves(moveSequence.slice(0, currentMoveIndex));
    } else {
      setCompletedMoves([]);
    }
    
    // Check if the sequence is complete
    if (currentMoveIndex >= moveSequence.length) {
      setSequenceStatus('completed');
    }
  }, [currentMoveIndex, moveSequence]);

  // Effect to handle sequence completion and call onComplete
  useEffect(() => {
    if (sequenceStatus === 'completed') {
      onComplete(true);
    } else if (sequenceStatus === 'failed') {
      onComplete(false);
    }
  }, [sequenceStatus, onComplete]);

  // Timer logic using useEffect
  useEffect(() => {
    // Only start the timer if a timeLimit is provided and the sequence is in progress
    if (!timeLimit || sequenceStatus !== 'in-progress') return;

    // Initialize timeRemaining with the timeLimit
    setTimeRemaining(timeLimit);

    // Set up the interval to update timeRemaining every second
    const timerInterval = setInterval(() => {
      setTimeRemaining(prev => {
        // If time is up, set sequence status to failed
        if (prev <= 1) {
          clearInterval(timerInterval);
          setSequenceStatus('failed');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Clean up the interval on unmount or when sequence status changes
    return () => clearInterval(timerInterval);
  }, [timeLimit, sequenceStatus]);

  /**
   * Handles the undo action.
   * Calls the onUndo prop if undo is enabled, available, and maxUndos not exceeded.
   * Increments the undosUsed counter if the onUndo callback is successful.
   */
  const handleUndo = useCallback(() => {
    if (!enableUndo || !onUndo || !canUndo) return;
    
    // Check if we've exceeded the maximum number of undos
    if (maxUndos !== undefined && undosUsed >= maxUndos) return;
    
    const undoSuccessful = onUndo();
    if (undoSuccessful) {
      setUndosUsed(prev => prev + 1);
    }
  }, [enableUndo, onUndo, canUndo, maxUndos, undosUsed]);

  // Format time as MM:SS
  const formatTime = useCallback((seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }, []);

  // Generate a progress percentage
  const progressPercentage = Math.round((currentMoveIndex / moveSequence.length) * 100);

  // Determine if undo button should be shown and enabled
  const showUndoButton = enableUndo && onUndo;
  const isUndoEnabled = canUndo && 
    (maxUndos === undefined || undosUsed < maxUndos) &&
    sequenceStatus === 'in-progress';

  return (
    <div className={`${styles.sequenceTracker} ${className || ''}`} data-testid="sequence-tracker">
      {/* Sequence progress information */}
      <div className={styles.progressInfo}>
        <div className={styles.moveCount}>
          <span className={styles.currentMove}>{currentMoveIndex}</span>
          <span className={styles.separator}>/</span>
          <span className={styles.totalMoves}>{moveSequence.length}</span>
          <span className={styles.movesLabel}>moves</span>
        </div>
        
        {/* Undo button */}
        {showUndoButton && (
          <div className={styles.undoContainer}>
            <button
              className={`${styles.undoButton} ${!isUndoEnabled ? styles.disabled : ''}`}
              onClick={handleUndo}
              disabled={!isUndoEnabled}
              data-testid="undo-button"
              title={
                maxUndos !== undefined 
                  ? `Undo (${maxUndos - undosUsed} remaining)`
                  : 'Undo last move'
              }
            >
              ↶ Undo
            </button>
            {maxUndos !== undefined && (
              <span className={styles.undoCounter} data-testid="undo-counter">
                {undosUsed}/{maxUndos}
              </span>
            )}
          </div>
        )}
        
        {/* Timer display (if enabled) */}
        {showTimer && (
          <div className={styles.timerContainer}>
            <div className={styles.timer} data-testid="sequence-timer">
              {formatTime(timeRemaining)}
            </div>
          </div>
        )}
      </div>
      
      {/* Progress bar */}
      <div className={styles.progressBarContainer}>
        <div 
          className={styles.progressBar} 
          style={{ width: `${progressPercentage}%` }}
          data-testid="sequence-progress-bar"
        />
      </div>
      
      {/* Move sequence display */}
      <div className={styles.moveSequence}>
        {moveSequence.map((move, index) => (
          <div 
            key={`move-${index}`}
            className={`
              ${styles.move}
              ${index < currentMoveIndex ? styles.completed : ''}
              ${index === currentMoveIndex ? styles.current : ''}
            `}
            data-testid={`sequence-move-${index}`}
          >
            {move}
          </div>
        ))}
      </div>
    </div>
  );
};

export default React.memo(SequenceTrackerInternal); 